import React, { useEffect, useState } from 'react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from './ui/dialog';
import { Button } from './ui/button';
import {
  ExternalLink,
  Copy,
  CheckCircle2,
  Loader2,
  Heart,
  MessageSquare,
  Radio,
  Video as VideoIcon,
  Image as ImageIcon,
  Lock,
  AlertTriangle,
} from 'lucide-react';
import api from '../api/client';
import { mediaUrl, thumbUrl } from '../api/posts';
import useAppConfig from '../hooks/useAppConfig';
import { useToast } from '../hooks/use-toast';

/**
 * Phase 51 · Admin Post Preview Modal.
 *
 * Opened from the "eye" icon in Content Moderation and Reports admin
 * tables. Loads the full post payload via the authenticated `/posts/:id`
 * endpoint so admins can preview paid content without having to buy it,
 * WITHOUT navigating away from the current list.
 *
 * Behaviour:
 *  - `postId` prop triggers the fetch on mount.
 *  - The dialog is fully controlled (`open` / `onOpenChange`) so callers
 *    can preserve their list state (scroll, filters, search).
 *  - Falls back to `fetchPublicPost` if the authenticated endpoint 404s
 *    (post may have been soft-deleted).
 *  - "Apri pagina pubblica" opens `/post/:id` in a new tab explicitly.
 */
const PostPreviewModal = ({ open, onOpenChange, postId }) => {
  const { peopleFeedEnabled } = useAppConfig();
  const { toast } = useToast();
  const [post, setPost] = useState(null);
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState('');
  const [copied, setCopied] = useState(false);
  // Phase 51 · Graceful fallback if the underlying image/video 404s
  // (e.g. media file was deleted from storage but the post row lingers).
  const [mediaBroken, setMediaBroken] = useState(false);

  useEffect(() => {
    if (!open || !postId) return undefined;
    let alive = true;
    setMediaBroken(false);
    (async () => {
      setLoading(true);
      setErr('');
      setPost(null);
      try {
        // Admins have access to any post via the private endpoint. If the
        // post is soft-removed we fall back to the public one for a graceful
        // "removed" state.
        let res;
        try {
          res = await api.get(`/posts/${postId}`);
        } catch (e) {
          if (e?.response?.status === 404) {
            res = await api.get(`/posts/${postId}/public`);
          } else {
            throw e;
          }
        }
        if (alive) setPost(res.data);
      } catch (_e) {
        if (alive) setErr('not_available');
      } finally {
        if (alive) setLoading(false);
      }
    })();
    return () => { alive = false; };
  }, [open, postId]);

  const copyShortId = async () => {
    const shortId = post?.short_id || (postId ? `NYP-${postId.replace(/-/g, '').toUpperCase().slice(0, 6)}` : '');
    if (!shortId) return;
    try {
      await navigator.clipboard.writeText(shortId);
      setCopied(true);
      setTimeout(() => setCopied(false), 1400);
    } catch {
      toast({ title: 'Impossibile copiare', variant: 'destructive' });
    }
  };

  const renderMedia = () => {
    if (!post) return null;
    const url = mediaUrl(post.media_url);
    const brokenCard = (
      <div className="flex aspect-video w-full flex-col items-center justify-center gap-1 rounded-lg border border-border bg-secondary text-center text-muted-foreground" data-testid="admin-preview-media-fallback">
        <Lock className="h-8 w-8" />
        <span className="text-xs">Nessuna anteprima disponibile</span>
      </div>
    );
    if (!url || mediaBroken) {
      return brokenCard;
    }
    if (post.media_type === 'video') {
      return (
        <video
          src={url}
          controls
          preload="metadata"
          onError={() => setMediaBroken(true)}
          className="max-h-[420px] w-full rounded-lg bg-black object-contain"
          data-testid="admin-preview-video"
        />
      );
    }
    if (post.media_type === 'image') {
      return (
        <img
          src={thumbUrl(post.media_url, 1080)}
          alt="post preview"
          className="max-h-[420px] w-full rounded-lg object-contain"
          loading="eager"
          decoding="async"
          onError={() => setMediaBroken(true)}
          data-testid="admin-preview-image"
        />
      );
    }
    // audio / other
    return (
      <audio
        controls
        src={url}
        onError={() => setMediaBroken(true)}
        className="w-full"
        data-testid="admin-preview-audio"
      />
    );
  };

  const shortId = post?.short_id || (postId ? `NYP-${postId.replace(/-/g, '').toUpperCase().slice(0, 6)}` : '');
  const authorHandle = post?.author?.username ? `@${post.author.username}` : '—';
  const kindIcon = post?.media_type === 'video' ? VideoIcon : post?.media_type === 'image' ? ImageIcon : Radio;
  const Kind = kindIcon;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-h-[90vh] overflow-y-auto sm:max-w-2xl" data-testid="admin-post-preview-modal">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Kind className="h-5 w-5 gold-text" />
            Anteprima post
          </DialogTitle>
        </DialogHeader>

        {loading && (
          <div className="flex items-center justify-center py-14">
            <Loader2 className="h-6 w-6 animate-spin gold-text" />
          </div>
        )}

        {err && !loading && (
          <div className="rounded-lg border border-amber-500/30 bg-amber-500/10 p-4 text-center text-sm">
            <AlertTriangle className="mx-auto mb-2 h-6 w-6 text-amber-500" />
            Post non disponibile o rimosso.
          </div>
        )}

        {!loading && !err && post && (
          <div className="space-y-4">
            <div className="flex flex-wrap items-center gap-x-3 gap-y-1 text-xs">
              <button
                onClick={copyShortId}
                className="inline-flex items-center gap-1 rounded-md border border-border bg-secondary px-2 py-0.5 font-mono hover:bg-secondary/80"
                data-testid="admin-preview-copy-shortid"
              >
                {shortId} {copied ? <CheckCircle2 className="h-3 w-3 text-emerald-500" /> : <Copy className="h-3 w-3" />}
              </button>
              <span className="text-muted-foreground">
                Autore: <span className="font-semibold text-foreground">{authorHandle}</span>
              </span>
              <span className="text-muted-foreground">
                Data: {new Date(post.created_at).toLocaleString('it-IT')}
              </span>
              <span
                className={`inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-[10px] font-semibold uppercase ${
                  post.is_paid ? 'bg-amber-500/15 gold-text' : 'bg-emerald-500/15 text-emerald-300'
                }`}
                data-testid="admin-preview-status-pill"
              >
                {post.is_paid ? `PAID · ${post.price} Cr` : 'FREE'}
              </span>
            </div>

            {renderMedia()}

            {post.caption && (
              <div className="rounded-lg border border-border bg-secondary/40 p-3 text-sm">
                <p className="mb-1 text-[10px] uppercase tracking-wide text-muted-foreground">Caption</p>
                <p className="whitespace-pre-wrap break-words">{post.caption}</p>
              </div>
            )}

            <div className="grid grid-cols-2 gap-3 rounded-lg border border-border bg-card p-3 text-sm">
              {!peopleFeedEnabled && (
                <div className="flex items-center gap-2">
                  <Heart className="h-4 w-4 text-red-400" />
                  <span className="text-muted-foreground">Like:</span>
                  <span className="font-semibold">{post.likes_count ?? 0}</span>
                </div>
              )}
              <div className="flex items-center gap-2">
                <MessageSquare className="h-4 w-4 gold-text" />
                <span className="text-muted-foreground">Commenti:</span>
                <span className="font-semibold">{post.comments_count ?? 0}</span>
              </div>
            </div>
          </div>
        )}

        <div className="mt-4 flex flex-col-reverse items-stretch justify-end gap-2 sm:flex-row sm:items-center">
          <Button
            variant="outline"
            onClick={() => onOpenChange(false)}
            data-testid="admin-preview-close-btn"
          >
            Chiudi
          </Button>
          {post && (
            <a
              href={`/post/${post.id}`}
              target="_blank"
              rel="noreferrer"
              className="inline-flex items-center justify-center gap-2 rounded-md border border-border px-3 py-2 text-sm font-medium hover:bg-secondary"
              data-testid="admin-preview-open-public-btn"
            >
              <ExternalLink className="h-4 w-4" /> Apri pagina pubblica
            </a>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default PostPreviewModal;
